/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useCallbackProfileData.ts (Callback Profiles)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for fetching and managing single Callback Profile data.
    Handles loading states and data adaptation to form state.

────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef } from 'react';
import { fetchCallbackProfileById } from '../api/callbackProfilesApi';
import { MappedFormState, CallbackProfileResponse } from '../types';
import { mapResponseToState } from '../utils/mappers';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';

/**
 * Hook for fetching Callback Profile data
 */
export function useCallbackProfileData(
  id: string | undefined,
  duplicateId: string | null
) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const [mappedData, setMappedData] = useState<MappedFormState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const activeRequestRef = useRef<{ id: string; requestId: number } | null>(null);
  const requestCounterRef = useRef(0);
  // Use refs to avoid including these in useEffect dependencies
  const showErrorRef = useRef(showError);
  const tRef = useRef(t);

  // Update refs when values change
  useEffect(() => {
    showErrorRef.current = showError;
    tRef.current = t;
  }, [showError, t]);

  useEffect(() => {
    const loadId = duplicateId || id;

    if (!loadId || loadId === 'new') {
      if (lastLoadedIdRef.current !== null) {
        lastLoadedIdRef.current = null;
        setMappedData(null);
      }
      return;
    }

    // Only load if ID actually changed
    if (lastLoadedIdRef.current === loadId) {
      return;
    }
    
    // Update ref immediately to prevent duplicate loads
    const currentLoadId = loadId;
    const requestId = ++requestCounterRef.current;
    lastLoadedIdRef.current = currentLoadId;
    activeRequestRef.current = { id: currentLoadId, requestId };
    setIsLoading(true);
    setMappedData(null); // Clear previous data immediately

    fetchCallbackProfileById(currentLoadId)
      .then((profile: CallbackProfileResponse) => {
        // Double-check that this is still the active request
        if (!activeRequestRef.current || activeRequestRef.current.requestId !== requestId) {
          return;
        }

        const mapped = mapResponseToState(profile);

        // If duplicating, modify the name
        if (duplicateId) {
          mapped.formState.name = `${mapped.formState.name} (Copy)`;
        }

        setMappedData(mapped);
      })
      .catch((error) => {
        if (!activeRequestRef.current || activeRequestRef.current.requestId !== requestId) {
          return;
        }
        const message = error instanceof Error ? error.message : tRef.current('callbackProfiles.messages.loadError');
        showErrorRef.current(message);
        lastLoadedIdRef.current = null;
        activeRequestRef.current = null;
        setMappedData(null);
      })
      .finally(() => {
        if (activeRequestRef.current?.requestId === requestId) {
          setIsLoading(false);
        }
      });

    return () => {
      // Only cancel if this is still the active request AND the ID changed
      // This prevents cancelling when React Strict Mode causes double execution
      if (activeRequestRef.current?.requestId === requestId && lastLoadedIdRef.current !== currentLoadId) {
        activeRequestRef.current = null;
      }
    };
  }, [id, duplicateId]); // Removed showError and t from dependencies

  return {
    mappedData,
    isLoading
  };
}

